/**
 * NL-042 / IMP-019 (2026-06-10) — weekly-review nudge on /check-in.
 *
 * From Step 8 the Sunday Weekly Review is part of the cadence. When the
 * current ISO week has no review content yet, this banner renders above
 * the Daily Check-in and links straight to /tools/weekly-review-v2.
 *
 * Self-gating like ConfidenceLogCard / WeeklyFocusCard: renders null for
 * clients below Step 8, while loading, or once this week's review has any
 * content. Deliberately independent of tool assignment — the cadence is
 * the trigger, not the coach's assignment list (per the NL-042 AC).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';

interface WeeklyReviewBannerProps {
  readonly clientId: string;
  readonly currentStep: number;
}

export function WeeklyReviewBanner({
  clientId,
  currentStep,
}: WeeklyReviewBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  // null = loading; true = current week already has content (hide);
  // false = no review yet (show the nudge).
  const [hasReview, setHasReview] = useState<boolean | null>(null);

  useEffect(() => {
    if (currentStep < 8 || !clientId) return;
    let active = true;
    void (async () => {
      try {
        const current = await api.getWeeklyReviewV2(accessToken, clientId, {});
        if (active) setHasReview(current?.hasAnyContent ?? false);
      } catch {
        // Best-effort: on error stay hidden rather than nag wrongly.
        if (active) setHasReview(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, currentStep]);

  if (currentStep < 8) return null;
  if (hasReview === null || hasReview) return null;

  return (
    <section
      className="mb-s5 rounded-lg border border-flame/40 bg-flame/[0.06] p-s5"
      data-testid="weekly-review-banner"
    >
      <div className="flex flex-wrap items-center justify-between gap-s3">
        <div>
          <h2 className="font-display text-display-sm text-ink">
            {t('checkIn.weeklyReview.title')}
          </h2>
          <p className="mt-s1 text-sm text-gray-2">
            {t('checkIn.weeklyReview.body')}
          </p>
        </div>
        <Link
          to={`/tools/weekly-review-v2?clientId=${encodeURIComponent(clientId)}`}
          className="rounded-md bg-flame-ember px-s4 py-s2 font-medium text-bone-1"
          data-testid="weekly-review-banner-cta"
        >
          {t('checkIn.weeklyReview.cta')}
        </Link>
      </div>
    </section>
  );
}
